/**
 * Field cache: one baked field per outline geometry (+ flow endpoints), least recently used evicted by bytes.
 * A flow field is per reference, not per outline — its endpoints are on the ref — so they are part of the key
 * (PLAN §Rocks). Cached fields are shared by every caller: treat `data` as read-only.
 *
 * Budget counts each field's `data` (16 B a cell, 4 MB at 512²). The baker's pooled working buffers do not count:
 * they are one fixed set, at most ≈ 6.8 MB at the 512² cap, whatever the number of fields cached.
 */

/** Default budget: 64 MB of field data. */
export const FIELD_CACHE_BYTES = 64 * 1024 * 1024
/** Nominal cost of a cached null (an outline with no area), so a stream of bad keys still ages out. */
const NULL_BYTES = 64

/** Cache key for an outline, optional flow endpoints and an optional grid cap (`maxSize`, a draft's). `outline.key` is stable per geometry (U1); `d` stands in. */
export function fieldKey(outline, flow, maxSize) {
	const base = outline.key ?? outline.d
	const key = flow ? `${base}|flow:${flow.from}:${flow.to}` : base
	return maxSize ? `${key}|max:${maxSize}` : key
}

/**
 * The warm set (`pin`) is what a cueable preset will draw: no LRU eviction takes a pinned key while an unpinned one can go instead. A warm set larger than the budget reports once through `onOverBudget` and then falls back to plain LRU over everything (DECISIONS 2026-10-07 plan: fields warm before a fade).
 * @param {(d: string, opts: { flow: { from: number, to: number } | null, maxSize?: number }) => object | null} bake
 * @param {number} [budget] bytes of field data to keep
 * @param {{ onOverBudget?: (info: { bytes: number, budget: number, pinned: number }) => void }} [options]
 */
export function createFieldCache(bake, budget = FIELD_CACHE_BYTES, { onOverBudget } = {}) {
	/** key → { field, bytes }; Map order is recency, oldest first. */
	const entries = new Map()
	let bytes = 0
	let pinned = new Set()
	// One report per pin set: the warm bakes the set slice by slice, and each bake past the budget would report again.
	let reported = false

	const drop = (k, e) => {
		entries.delete(k)
		bytes -= e.bytes
	}

	// Oldest unpinned first; then, only if the pinned set alone is over, oldest of anything. `keep` (the entry just added) always stays, even alone over budget.
	const evict = keep => {
		for (const [k, e] of entries) {
			if (bytes <= budget) return
			if (k !== keep && !pinned.has(k)) drop(k, e)
		}
		if (bytes <= budget || entries.size <= 1) return
		if (!reported) {
			reported = true
			onOverBudget?.({ bytes, budget, pinned: pinned.size })
		}
		for (const [k, e] of entries) {
			if (bytes <= budget) return
			if (k !== keep) drop(k, e)
		}
	}

	/**
	 * @param {{ d: string, key?: string } | null} outline as resolveOutline returns it
	 * @param {{ from: number, to: number } | null} [flow]
	 * @param {number} [maxSize] the grid's long side; omitted, the baker's default
	 */
	function get(outline, flow = null, maxSize) {
		if (!outline || typeof outline.d !== 'string') return null
		const key = fieldKey(outline, flow, maxSize)
		const hit = entries.get(key)
		if (hit) {
			entries.delete(key)
			entries.set(key, hit)
			return hit.field
		}
		const field = bake(outline.d, { flow: flow || null, ...(maxSize && { maxSize }) })
		const size = field ? field.data.byteLength : NULL_BYTES
		entries.set(key, { field, bytes: size })
		bytes += size
		evict(key)
		return field
	}

	return {
		get,
		/** Cached under this key, without baking or touching recency. */
		has: key => entries.has(key),
		/** Replace the warm set (field keys, as `fieldKey` makes them). An unpinned key ages out as usual; nothing is evicted here. */
		pin(keys) {
			pinned = new Set(keys)
			reported = false
		},
		clear() {
			entries.clear()
			bytes = 0
		},
		/** @returns {{ entries: number, bytes: number, keys: string[] }} keys oldest first */
		stats: () => ({ entries: entries.size, bytes, keys: [...entries.keys()] }),
		/** @returns {string[]} the warm keys currently cached */
		pinnedKeys: () => [...pinned].filter(k => entries.has(k)),
	}
}
